<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { AxiosAPI } from '@/axios';
import { useMainStore } from '@/stores/index';
import moment from 'moment';
import Breadcrumb from '../patchName.vue';
import DocumentTableLayout from './DocumentTableLayout.vue';
import SectorFlowBadge from '../AtomComponents/SectorFlowBadge.vue';

defineOptions({
  name: 'AllDocuments'
});

const Store = useMainStore();
Store.Token();
const router = useRouter();

const rawDocuments = ref([]);
const loading = ref(true);

const searchStatusSelect = ref(0);
const onlyPendingMySignature = ref(false);

const statusOptions = [
  { id: 0, name: 'Selecione' },
  { id: 1, name: 'Em movimentação' },
  { id: 2, name: 'Arquivado' }
];

const showSignModal = ref(false);
const documentToSign = ref(null);
const password = ref('');
const wrongPassword = ref(false);
const signingProgress = ref(0);
const isSigning = ref(false);
const signerSuccessMessage = ref('');

const formatDate = (value) => {
  if (!value) return '-';
  moment.locale('pt-br');
  return moment(value).format('DD/MM/YYYY');
};

const mapDocument = (doc) => {
  const isPendingSign = doc.signers?.some(
    (s) => Number(s.user_id) === Number(Store.user?.id) && String(s.signature_status) === '0'
  );

  const isFiled =
    doc.status === 'filed' ||
    Boolean(doc.archived) ||
    String(doc.archived) === '1' ||
    Number(doc.is_archived) === 1 ||
    Boolean(doc.filed);

  let statusText = 'Em movimentação';
  let statusBadgeClass = 'badge-status-movement';

  if (isFiled) {
    statusText = 'Arquivado';
    statusBadgeClass = 'badge-status-filed';
  } else if (isPendingSign || doc.status === 'pending') {
    statusText = 'Aguardando assinatura';
    statusBadgeClass = 'badge-status-pending';
  }

  const authorName = doc.author?.user?.name || doc.author?.name || 'Remetente Desconhecido';
  const authorRole = doc.author?.user?.role || doc.author?.role || '';

  return {
    ...doc,
    formattedDate: formatDate(doc.created_at),
    isPendingSign,
    statusText,
    statusBadgeClass,
    authorName,
    authorRole,
    isRead: Boolean(doc.read || doc.is_read || doc.status_read)
  };
};

const fetchDocuments = async () => {
  loading.value = true;
  rawDocuments.value = [];

  const params = {};
  if (searchStatusSelect.value === 1) params.status = 'movement';
  if (searchStatusSelect.value === 2) params.status = 'filed';

  try {
    const response = await AxiosAPI.get('/api/documents', {
      params,
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + Store.accessToken
      }
    });

    const list = response.data?.documents?.data || response.data?.documents || [];
    rawDocuments.value = list.map(mapDocument);
  } catch (error) {
    console.error('Erro ao buscar todos os documentos:', error);
  } finally {
    loading.value = false;
  }
};

const toggleFavorite = (doc) => {
  const previousState = doc.favorited;
  doc.favorited = !previousState;

  const endpoint = `/api/documents/${doc.id}/favorite`;
  const request = previousState
    ? AxiosAPI.delete(endpoint, { headers: { Authorization: 'Bearer ' + Store.accessToken } })
    : AxiosAPI.post(endpoint, {}, { headers: { Authorization: 'Bearer ' + Store.accessToken } });

  request.catch(() => {
    doc.favorited = previousState;
  });
};

const handleArchivedBatchSuccess = (count) => {
  signerSuccessMessage.value = `${count} documento(s) arquivado(s) com sucesso!`;
  fetchDocuments();
};

const handleUnarchivedBatchSuccess = (count) => {
  signerSuccessMessage.value = `${count} documento(s) desarquivado(s) com sucesso!`;
  fetchDocuments();
};

const processedDocuments = computed(() => {
  let docs = rawDocuments.value;
  if (onlyPendingMySignature.value) {
    docs = docs.filter((d) => d.isPendingSign);
  }
  return docs;
});

const openSignModal = (doc) => {
  documentToSign.value = doc;
  password.value = '';
  wrongPassword.value = false;
  showSignModal.value = true;
};

const executeSign = async () => {
  if (!password.value || !documentToSign.value) return;

  isSigning.value = true;
  signingProgress.value = 20;

  const interval = setInterval(() => {
    if (signingProgress.value < 90) {
      signingProgress.value += 10;
    }
  }, 400);

  try {
    await AxiosAPI.post(
      `api/documents/${documentToSign.value.id}/sign`,
      { user_id: Store.user.id, password: password.value },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + Store.accessToken
        }
      }
    );

    signingProgress.value = 100;
    clearInterval(interval);

    setTimeout(() => {
      showSignModal.value = false;
      isSigning.value = false;
      signerSuccessMessage.value = `${documentToSign.value.protocol || 'Documento'} assinado com sucesso!`;
      fetchDocuments();
    }, 1000);
  } catch (error) {
    clearInterval(interval);
    isSigning.value = false;
    signingProgress.value = 0;
    wrongPassword.value = true;
  }
};

const navigateToView = (doc) => {
  Store.viewDispatch = doc.dispatch;
  router.push({ name: 'EnviadosView', params: { id: doc.protocol } });
};

onMounted(() => {
  Store.DepartamentosAll();
  fetchDocuments();
});
</script>

<template>
  <div class="py-4 container-fluid" id="AllDocs">
    <Breadcrumb />

    <h4 class="page-title mb-4">Todos os documentos</h4>

    <div
      v-if="signerSuccessMessage"
      class="alert-success-custom d-flex justify-content-between align-items-center mb-4 px-3"
    >
      <div class="d-flex align-items-center">
        <font-awesome-icon :icon="['fas', 'check-circle']" class="me-2 icon-success" />
        <span class="font-docs text-success-dark"><b>{{ signerSuccessMessage }}</b></span>
      </div>
      <button type="button" class="btn-close-alert" @click="signerSuccessMessage = ''">
        <font-awesome-icon :icon="['fas', 'xmark']" />
      </button>
    </div>

    <DocumentTableLayout
      title="Todos os documentos"
      :items="processedDocuments"
      :loading="loading"
      :selectable="true"
      searchPlaceholder="Assunto, protocolo..."
      :searchFilterKeys="['subject', 'protocol']"
      @search="fetchDocuments"
      @archived="handleArchivedBatchSuccess"
      @unarchived="handleUnarchivedBatchSuccess"
    >
      <template #extra-filters>
        <div class="col-12 col-md-4 col-lg-3">
          <label class="filter-label mb-1">Situação</label>
          <select
            v-model="searchStatusSelect"
            @change="fetchDocuments"
            class="form-select select-custom"
          >
            <option v-for="opt in statusOptions" :key="opt.id" :value="opt.id">
              {{ opt.name }}
            </option>
          </select>
        </div>

        <div class="col-12 col-md-5 col-lg-4 d-flex align-items-center pb-1">
          <div class="form-check mt-3 mt-md-0">
            <input
              type="checkbox"
              id="pendingMySign"
              v-model="onlyPendingMySignature"
              class="form-check-input check-orange"
            />
            <label for="pendingMySign" class="form-check-label font-docs fs-7 text-secondary cursor-pointer">
              Mostrar só pendentes de minha assinatura
            </label>
          </div>
        </div>
      </template>

      <template #header>
        <th scope="col" style="width: 5%;"></th>
        <th scope="col" style="width: 15%;">Protocolo</th>
        <th scope="col" style="width: 35%;">Documento</th>
        <th scope="col" style="width: 12%;">Data</th>
        <th scope="col" style="width: 15%;">Remetente</th>
        <th scope="col" style="width: 13%;">Situação</th>
        <th scope="col" class="text-end" style="width: 5%;"></th>
      </template>

      <template #row="{ item }">
        <td class="text-center">
          <font-awesome-icon
            :icon="[item.favorited ? 'fas' : 'far', 'star']"
            :class="['favorite-star', { active: item.favorited }]"
            @click.stop="toggleFavorite(item)"
          />
        </td>

        <td class="fw-bold text-dark font-docs font-size-13">
          {{ item.protocol || '-' }}
        </td>

        <td class="font-docs">
          <div class="d-flex flex-column gap-1">
            <span class="cell-doc text-truncate" style="max-width: 300px;">
              {{ item.subject || 'Sem assunto' }}
            </span>
            <SectorFlowBadge :document="item" />
          </div>
        </td>

        <td class="cell-text font-docs">
          {{ item.formattedDate }}
        </td>

        <td class="font-docs">
          <div class="d-flex flex-column">
            <span class="fw-600 text-dark font-size-13">{{ item.authorName }}</span>
            <small v-if="item.authorRole" class="text-muted font-size-12">{{ item.authorRole }}</small>
          </div>
        </td>

        <td class="font-docs">
          <div class="d-inline-flex align-items-center gap-2">
            <span :class="['badge-status', item.statusBadgeClass]">
              {{ item.statusText }}
            </span>

            <button
              v-if="item.isPendingSign"
              type="button"
              class="btn-action btn-orange p-0 ms-1"
              title="Assinar Documento"
              @click.stop="openSignModal(item)"
            >
              <font-awesome-icon :icon="['fas', 'pen']" />
            </button>
          </div>
        </td>

        <td class="text-end">
          <button
            type="button"
            class="btn-action btn-next"
            title="Acessar"
            @click="navigateToView(item)"
          >
            <font-awesome-icon :icon="['fas', 'chevron-right']" />
          </button>
        </td>
      </template>

      <template #actions="{ item }">
        <div class="d-flex align-items-center gap-2 w-100 justify-content-between">
          <div class="d-flex align-items-center gap-2">
            <font-awesome-icon
              :icon="[item.favorited ? 'fas' : 'far', 'star']"
              :class="['favorite-star', { active: item.favorited }]"
              @click.stop="toggleFavorite(item)"
            />
            <span :class="['badge-status', item.statusBadgeClass]">
              {{ item.statusText }}
            </span>
          </div>

          <div class="d-flex align-items-center gap-2">
            <button
              v-if="item.isPendingSign"
              type="button"
              class="btn-action btn-orange"
              title="Assinar Documento"
              @click.stop="openSignModal(item)"
            >
              <font-awesome-icon :icon="['fas', 'pen']" />
            </button>

            <button
              type="button"
              class="btn-action btn-next"
              title="Acessar"
              @click="navigateToView(item)"
            >
              <font-awesome-icon :icon="['fas', 'chevron-right']" />
            </button>
          </div>
        </div>
      </template>
    </DocumentTableLayout>

    <div
      v-if="showSignModal"
      class="modal-backdrop-custom d-flex align-items-center justify-content-center"
    >
      <div class="modal-card p-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="fw-bold text-dark font-docs mb-0">Assinar Documento</h5>
          <button type="button" class="btn-close-alert" @click="showSignModal = false">
            <font-awesome-icon :icon="['fas', 'xmark']" />
          </button>
        </div>

        <div v-if="!isSigning">
          <div class="mb-3">
            <label class="filter-label mb-1">Sua senha <span class="text-danger">*</span></label>
            <input
              type="password"
              v-model="password"
              class="form-control input-search"
              placeholder="Sua senha do Smart2doc"
              @keyup.enter="executeSign"
            />
            <small v-if="wrongPassword" class="text-danger mt-1 d-block font-docs">
              Senha incorreta. Tente novamente.
            </small>
          </div>

          <div class="d-flex gap-2 justify-content-end mt-4">
            <button
              type="button"
              class="btn btn-light-custom font-docs"
              @click="showSignModal = false"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-orange-custom font-docs"
              @click="executeSign"
            >
              Assinar
            </button>
          </div>
        </div>

        <div v-else class="text-center py-3">
          <p class="font-docs text-secondary mb-2">Assinando documento, aguarde...</p>
          <div class="progress" style="height: 12px;">
            <div
              class="progress-bar bg-orange-progress"
              role="progressbar"
              :style="{ width: signingProgress + '%' }"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-title {
  color: #212529;
  font-weight: 600;
  font-size: 22px;
}

.filter-label {
  font-size: 13px;
  color: #6c757d;
  font-weight: 500;
}

.select-custom {
  font-size: 14px;
  border-color: #dee2e6;
  border-radius: 6px;
}

.select-custom:focus {
  border-color: #f1860b;
  box-shadow: 0 0 0 0.2rem rgba(241, 134, 11, 0.15);
}

.check-orange:checked {
  background-color: #f1860b;
  border-color: #f1860b;
}

.favorite-star {
  color: #ced4da;
  cursor: pointer;
  font-size: 15px;
  transition: color 0.15s ease;
}

.favorite-star.active {
  color: #f1860b;
}

.badge-status {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  display: inline-block;
}

.badge-status-pending {
  background-color: #fef3c7;
  color: #d97706;
}

.badge-status-movement {
  background-color: #e0f2fe;
  color: #0284c7;
}

.badge-status-filed {
  background-color: #f3f4f6;
  color: #6b7280;
}

.btn-action {
  background: transparent;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
}

.btn-orange {
  color: #f1860b;
}

.btn-orange:hover {
  background-color: rgba(241, 134, 11, 0.1);
}

.btn-next {
  color: #a0aec0;
}

.btn-next:hover {
  color: #212529;
}

.alert-success-custom {
  background-color: #badbcc;
  height: 50px;
  border-radius: 8px;
}

.text-success-dark {
  color: #0f5132;
}

.btn-close-alert {
  background: transparent;
  border: none;
  color: #7d787f;
  cursor: pointer;
}

.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.45);
  z-index: 1050;
  padding: 16px;
}

.modal-card {
  background-color: #ffffff;
  border-radius: 12px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.btn-light-custom {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  color: #495057;
  padding: 8px 16px;
  border-radius: 6px;
}

.btn-orange-custom {
  background-color: #f1860b;
  color: #ffffff;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
}

.btn-orange-custom:hover {
  background-color: #d97405;
}

.bg-orange-progress {
  background-color: #f1860b;
}

.fw-600 {
  font-weight: 600;
}
</style>